// Tool-specific FAQs for enhanced SEO and AI scraping

export interface ToolFAQ {
  question: string;
  answer: string;
}

export interface ToolEnhancement {
  howToUse: {
    steps: string[];
  };
  faqs: ToolFAQ[];
}

export const toolEnhancements: Record<string, ToolEnhancement> = {
  "email-checker": {
    howToUse: {
      steps: [
        "Click 'Create Test Session' to generate a unique test email address",
        "Send an email from your domain to the generated test address",
        "Wait for the analysis (usually 30-60 seconds)",
        "Review your SPF, DKIM, DMARC results and spam score",
      ],
    },
    faqs: [
      {
        question: "What is a good spam score?",
        answer: "A spam score below 3 is considered good. Scores above 5 indicate high spam risk and your emails may land in spam folders.",
      },
      {
        question: "Why does my email fail SPF authentication?",
        answer: "SPF failures occur when the sending server's IP isn't listed in your domain's SPF record. Update your DNS to include all authorized sending IPs.",
      },
      {
        question: "How often should I test my deliverability?",
        answer: "Test after any DNS changes, before major campaigns, and monthly as a routine check. Sudden drops in open rates also warrant testing.",
      },
    ],
  },
  "auth-checker": {
    howToUse: {
      steps: [
        "Enter your domain name (e.g., yourdomain.com)",
        "Click 'Check Authentication'",
        "Review SPF, DKIM, and DMARC record status",
        "Follow recommendations to fix any issues",
      ],
    },
    faqs: [
      {
        question: "What does 'softfail' mean in SPF?",
        answer: "Softfail (~all) tells receiving servers to accept but flag emails from unauthorized IPs. It's less strict than hardfail (-all) but provides some protection.",
      },
      {
        question: "How do I find my DKIM selector?",
        answer: "Your DKIM selector is typically provided by your email service (e.g., 'google' for Google Workspace, 'selector1' for Microsoft 365). Check your provider's documentation.",
      },
      {
        question: "What DMARC policy should I use?",
        answer: "Start with p=none for monitoring, move to p=quarantine after analyzing reports, and finally p=reject for maximum protection once you're confident in your configuration.",
      },
    ],
  },
  "blacklist-checker": {
    howToUse: {
      steps: [
        "Enter your domain name or IP address",
        "Click 'Check Blacklists'",
        "Review results from major blocklist providers",
        "If listed, follow delisting procedures for each blocklist",
      ],
    },
    faqs: [
      {
        question: "How do I get removed from a blacklist?",
        answer: "Each blacklist has its own delisting process. Most require you to fix the underlying issue (compromised account, spam reports) and submit a removal request through their website.",
      },
      {
        question: "Why is my IP blacklisted when I haven't sent spam?",
        answer: "Shared IP addresses (common with hosting providers) can be blacklisted due to other users' behavior. Consider using a dedicated IP for sending email.",
      },
      {
        question: "How long does delisting take?",
        answer: "Delisting times vary: some lists remove you within hours after a request, while others have automatic expiration after 24-72 hours if no new spam is detected.",
      },
    ],
  },
  "header-analyzer": {
    howToUse: {
      steps: [
        "Copy the full email headers from your email client",
        "Paste headers into the analyzer",
        "Click 'Analyze Headers'",
        "Review the decoded routing, authentication, and spam indicators",
      ],
    },
    faqs: [
      {
        question: "How do I get email headers?",
        answer: "In Gmail: Open email → Three dots → 'Show original'. In Outlook: Open email → File → Properties → Internet headers. Each client has a similar 'View source' option.",
      },
      {
        question: "What is the Received chain?",
        answer: "The Received headers show the path your email took from sender to recipient. Each mail server adds a Received line, creating a chain that helps diagnose delivery issues.",
      },
      {
        question: "What does X-Spam-Status mean?",
        answer: "X-Spam-Status shows the spam filter's verdict and score. It lists the tests that were triggered and their individual scores that contributed to the final decision.",
      },
    ],
  },
  "tab-predictor": {
    howToUse: {
      steps: [
        "Paste your email subject line and body content",
        "Click 'Analyze'",
        "Review the Primary vs Promotions prediction",
        "Follow suggestions to improve Primary tab placement",
      ],
    },
    faqs: [
      {
        question: "Why do emails go to Promotions tab?",
        answer: "Gmail uses machine learning to detect marketing signals: unsubscribe links, HTML templates, promotional language, and sender reputation. Personal-style emails are more likely to hit Primary.",
      },
      {
        question: "Does the Promotions tab hurt open rates?",
        answer: "Research shows 68.8% of users check their Promotions tab regularly. While Primary has higher open rates, Promotions tab emails still achieve 19% average open rates.",
      },
      {
        question: "Can I force emails to go to Primary?",
        answer: "You can't force placement, but you can increase Primary likelihood by: sending plain text, avoiding marketing templates, personalizing content, and building engagement history.",
      },
    ],
  },
  "subject-analyzer": {
    howToUse: {
      steps: [
        "Enter your subject line or email body text",
        "Click 'Analyze for Spam Triggers'",
        "Review flagged words and phrases",
        "Rewrite content to avoid spam filter triggers",
      ],
    },
    faqs: [
      {
        question: "What words trigger spam filters?",
        answer: "Common triggers include: 'FREE', 'Act now', 'Limited time', 'Guaranteed', excessive punctuation (!!!), ALL CAPS, and certain financial terms. Context matters—one trigger rarely causes issues.",
      },
      {
        question: "Does subject line length affect spam scoring?",
        answer: "Very long subject lines (50+ characters) can trigger filters and get truncated on mobile. Aim for 30-50 characters for optimal deliverability and readability.",
      },
      {
        question: "Are emojis in subject lines spam triggers?",
        answer: "Moderate emoji use is generally fine, but excessive emojis (3+) or certain symbols can increase spam scores. Test with your specific audience and email provider.",
      },
    ],
  },
  "phishing-detector": {
    howToUse: {
      steps: [
        "Paste the suspicious email content",
        "Click 'Analyze for Phishing'",
        "Review detected red flags and risk score",
        "Use findings to determine if the email is legitimate",
      ],
    },
    faqs: [
      {
        question: "What are common phishing indicators?",
        answer: "Key red flags: urgent language, mismatched sender domains, suspicious links, generic greetings, spelling errors, requests for sensitive information, and impersonation of known brands.",
      },
      {
        question: "How do I verify a suspicious link?",
        answer: "Hover over links to preview the URL (don't click). Use our Link Validator tool to check the destination. Legitimate companies use their official domains, not URL shorteners.",
      },
      {
        question: "What should I do if I clicked a phishing link?",
        answer: "Immediately: change passwords for any accounts you entered, enable 2FA, scan for malware, monitor accounts for suspicious activity, and report the phishing attempt to your IT team.",
      },
    ],
  },
  "dmarc-analyzer": {
    howToUse: {
      steps: [
        "Export your DMARC aggregate report (XML format)",
        "Upload or paste the report content",
        "Click 'Analyze Report'",
        "Review authentication pass/fail statistics and source IPs",
      ],
    },
    faqs: [
      {
        question: "What is a DMARC aggregate report?",
        answer: "DMARC aggregate reports are daily/weekly XML files sent by receiving mail servers showing how your domain's emails performed against SPF and DKIM checks.",
      },
      {
        question: "What does alignment mean in DMARC?",
        answer: "Alignment means the domain in the From header matches the domains authenticated by SPF and DKIM. Strict alignment requires exact match; relaxed allows subdomains.",
      },
      {
        question: "Why am I seeing failed authentications from legitimate sources?",
        answer: "Common causes: email forwarding breaks SPF, third-party services not configured in SPF, DKIM not set up for all sending services, or misconfigured subdomains.",
      },
    ],
  },
  "domain-age-checker": {
    howToUse: {
      steps: [
        "Enter the domain name you want to check",
        "Click 'Check Domain Age'",
        "View registration date and domain age",
        "Consider age when evaluating sender reputation",
      ],
    },
    faqs: [
      {
        question: "Why does domain age matter for email?",
        answer: "Older domains have established reputation history. New domains (under 30 days) are often flagged as higher risk by spam filters and may face deliverability challenges.",
      },
      {
        question: "How old should a domain be before sending cold emails?",
        answer: "Wait at least 2-4 weeks after registration before any sending, then warm up gradually over 4-8 weeks. Domains under 90 days old should send conservatively.",
      },
      {
        question: "Can I check any domain's age?",
        answer: "Most domains' WHOIS data is public, but some use privacy protection. We use RDAP (the modern WHOIS replacement) which provides more reliable data.",
      },
    ],
  },
  "pm-cost-calculator": {
    howToUse: {
      steps: [
        "Enter your team size",
        "Select the tools you want to compare",
        "Review annual cost comparison",
        "Click through to learn more about each option",
      ],
    },
    faqs: [
      {
        question: "Why is Lottae the cheapest option?",
        answer: "Lottae offers flat $3.50/user pricing with no feature tiers or restrictions. Competitors like Monday and Asana charge $10-25/user and gate features behind expensive plans.",
      },
      {
        question: "Are there hidden costs with PM tools?",
        answer: "Watch for: per-seat pricing jumps between tiers, automation limits, integration add-ons, storage limits, and guest user fees. Lottae includes everything in one price.",
      },
      {
        question: "How do I calculate the true cost of switching tools?",
        answer: "Factor in: migration time (1-2 weeks), training (2-4 hours/person), temporary productivity loss (10-20%), and data export/import effort. Most switches pay off within 3-6 months.",
      },
    ],
  },
  "webhook-debugger": {
    howToUse: {
      steps: [
        "Click 'Create Webhook URL' to generate a unique endpoint",
        "Copy the URL and configure it in your service",
        "Trigger the webhook from your application",
        "View incoming requests in real-time below",
      ],
    },
    faqs: [
      {
        question: "How long do webhook URLs stay active?",
        answer: "Generated URLs remain active for 24 hours. After expiration, you can create a new URL. Request history is stored for the duration of the session.",
      },
      {
        question: "Can I test webhooks from localhost?",
        answer: "Yes! Use our generated URL as your webhook endpoint. Your local app sends to our URL, and we capture/display the request—no tunneling software needed.",
      },
      {
        question: "What HTTP methods are supported?",
        answer: "We capture all HTTP methods: GET, POST, PUT, PATCH, DELETE. We store the full request including headers, body, and query parameters.",
      },
    ],
  },
  "api-response-simulator": {
    howToUse: {
      steps: [
        "Configure your desired response (status code, body, headers)",
        "Click 'Create Mock Endpoint'",
        "Copy the generated URL",
        "Use the URL in your frontend code for testing",
      ],
    },
    faqs: [
      {
        question: "How do I simulate API errors?",
        answer: "Set the status code to 400, 401, 403, 404, or 500 to test how your frontend handles different error scenarios. Add appropriate error messages in the response body.",
      },
      {
        question: "Can I add response delays?",
        answer: "Yes! Set a delay in milliseconds to simulate slow API responses. This helps test loading states and timeout handling in your application.",
      },
      {
        question: "How long do mock endpoints last?",
        answer: "Mock endpoints remain active for 24 hours. They're perfect for development and testing but shouldn't be used for production workloads.",
      },
    ],
  },
  "global-latency-tester": {
    howToUse: {
      steps: [
        "Enter the URL you want to test",
        "Click 'Test Latency'",
        "View response times from 8 global regions",
        "Identify regions with performance issues",
      ],
    },
    faqs: [
      {
        question: "What is a good response time?",
        answer: "Under 200ms is excellent, 200-500ms is acceptable, over 500ms may impact user experience. Times vary by region—optimize for your primary audience's location.",
      },
      {
        question: "Why are some regions slower?",
        answer: "Distance from your server, CDN coverage, network routing, and regional infrastructure all affect latency. Consider using a CDN or edge servers for global audiences.",
      },
      {
        question: "How can I improve global performance?",
        answer: "Use a CDN (Cloudflare, Fastly), deploy to multiple regions, optimize server response time, reduce payload size, and implement caching strategies.",
      },
    ],
  },
  // Add more tools as needed...
  "content-analyzer": {
    howToUse: {
      steps: [
        "Paste your email HTML content",
        "Click 'Analyze Accessibility'",
        "Review mobile rendering and accessibility scores",
        "Implement suggested improvements",
      ],
    },
    faqs: [
      {
        question: "What makes an email accessible?",
        answer: "Accessible emails include: alt text for images, proper heading hierarchy, sufficient color contrast, meaningful link text, and logical reading order for screen readers.",
      },
      {
        question: "Why is mobile optimization important?",
        answer: "Over 60% of emails are opened on mobile devices. Emails that don't render well on mobile see significantly lower engagement and higher unsubscribe rates.",
      },
      {
        question: "What is a good accessibility score?",
        answer: "Aim for 90+ on our accessibility check. Key factors include image alt text, color contrast ratios (4.5:1 minimum), and proper semantic structure.",
      },
    ],
  },
  "email-preview": {
    howToUse: {
      steps: [
        "Paste your email HTML code",
        "Select email clients to preview",
        "Review how your email renders",
        "Identify and fix rendering issues",
      ],
    },
    faqs: [
      {
        question: "Why does my email look different in Outlook?",
        answer: "Outlook uses Word's rendering engine, which has limited CSS support. Common issues: background images, margins, flexbox. Use tables for layout and inline styles.",
      },
      {
        question: "What email clients should I test?",
        answer: "Prioritize: Gmail (web and mobile), Apple Mail, Outlook (desktop and web), and Yahoo. These cover 90%+ of your audience for most B2B and B2C campaigns.",
      },
      {
        question: "How do I fix dark mode issues?",
        answer: "Use @media (prefers-color-scheme: dark) queries, avoid pure white backgrounds, and test colors in both modes. Some clients auto-invert colors unpredictably.",
      },
    ],
  },
  "dns-propagation-checker": {
    howToUse: {
      steps: [
        "Enter your domain name",
        "Select the DNS record type (A, CNAME, MX, TXT, etc.)",
        "Click 'Check Propagation'",
        "View results from global DNS servers",
      ],
    },
    faqs: [
      {
        question: "How long does DNS propagation take?",
        answer: "Most changes propagate within 24-48 hours, though some may take up to 72 hours. TTL (Time To Live) settings affect how quickly caches refresh.",
      },
      {
        question: "Why do some servers show old records?",
        answer: "DNS servers cache records based on TTL. Lower TTL values (300-600 seconds) speed up propagation but increase DNS query load. Higher TTLs (3600+) cache longer.",
      },
      {
        question: "What if propagation is stuck?",
        answer: "Try flushing your local DNS cache. If issues persist, verify the records at your registrar are correct and wait for the full TTL period to expire.",
      },
    ],
  },
  "seo-meta-analyzer": {
    howToUse: {
      steps: [
        "Enter the URL you want to analyze",
        "Click 'Analyze SEO'",
        "Review title, description, and Open Graph tags",
        "Implement suggested improvements",
      ],
    },
    faqs: [
      {
        question: "What is the ideal title tag length?",
        answer: "Keep title tags under 60 characters to avoid truncation in search results. Include your primary keyword near the beginning for maximum impact.",
      },
      {
        question: "Why are Open Graph tags important?",
        answer: "OG tags control how your pages appear when shared on social media. Without them, platforms may display incorrect images or descriptions.",
      },
      {
        question: "What makes a good meta description?",
        answer: "Under 160 characters, includes target keyword, has a clear call-to-action, and accurately describes page content. Good descriptions improve click-through rates.",
      },
    ],
  },
  "robots-checker": {
    howToUse: {
      steps: [
        "Enter the URL you want to check",
        "Click 'Analyze Robots Directives'",
        "Review robots.txt and meta robots tags",
        "Verify your indexing configuration",
      ],
    },
    faqs: [
      {
        question: "What is noindex vs nofollow?",
        answer: "Noindex prevents the page from appearing in search results. Nofollow tells search engines not to follow links on the page. They serve different purposes.",
      },
      {
        question: "Should I block my staging site?",
        answer: "Yes! Use robots.txt to Disallow all crawlers on staging/dev environments. Also use noindex meta tags and consider password protection for extra security.",
      },
      {
        question: "Why isn't my page being indexed?",
        answer: "Common causes: noindex tag, robots.txt blocking, canonical pointing elsewhere, low-quality content, or the page is too new. Use Google Search Console for diagnosis.",
      },
    ],
  },
  "link-validator": {
    howToUse: {
      steps: [
        "Enter the URL you want to validate",
        "Click 'Validate Link'",
        "Review redirect chain and safety status",
        "Check if the destination is blocklisted",
      ],
    },
    faqs: [
      {
        question: "What is a redirect chain?",
        answer: "A redirect chain occurs when a URL redirects multiple times before reaching the final destination. Long chains slow page load and can lose link equity for SEO.",
      },
      {
        question: "How do you detect malicious links?",
        answer: "We check against known blocklists, analyze the destination domain reputation, and flag suspicious patterns like URL shorteners hiding the final destination.",
      },
      {
        question: "Are URL shorteners safe to use?",
        answer: "URL shorteners obscure the destination, making them popular for phishing. In emails, avoid shorteners—use full URLs or branded short domains you control.",
      },
    ],
  },
  "m365-tenant-analyzer": {
    howToUse: {
      steps: [
        "Enter a domain associated with the tenant",
        "Click 'Analyze Tenant'",
        "View tenant ID, associated domains, and DNS records",
        "Use for vendor security assessment or troubleshooting",
      ],
    },
    faqs: [
      {
        question: "What is a Microsoft 365 tenant?",
        answer: "A tenant is an organization's dedicated instance of Microsoft 365 services. Each tenant has a unique ID and can have multiple domains associated with it.",
      },
      {
        question: "Why would I need to look up tenant info?",
        answer: "Useful for: verifying a vendor's legitimate Microsoft setup, troubleshooting email delivery issues, security research, or identifying all domains in an organization.",
      },
      {
        question: "Does this work for Google Workspace?",
        answer: "Yes! We detect both Microsoft 365 and Google Workspace configurations from DNS records, helping you understand an organization's email infrastructure.",
      },
    ],
  },
  "bulk-subject-analyzer": {
    howToUse: {
      steps: [
        "Enter multiple subject lines (one per line)",
        "Click 'Analyze All'",
        "Compare spam scores side-by-side",
        "Choose the subject line with the lowest risk",
      ],
    },
    faqs: [
      {
        question: "How many subject lines can I compare?",
        answer: "You can compare up to 10 subject lines at once. This is perfect for A/B testing preparation or comparing variations of your campaign.",
      },
      {
        question: "What factors affect the spam score?",
        answer: "We analyze: spam trigger words, punctuation patterns, capitalization, length, urgency language, and known phrases that correlate with spam.",
      },
      {
        question: "Should I always choose the lowest score?",
        answer: "Not necessarily. Balance spam risk with engagement potential. A slightly higher score with compelling copy may outperform a 'safe' but boring subject line.",
      },
    ],
  },
  "meeting-cost-calculator": {
    howToUse: {
      steps: [
        "Enter the number of attendees",
        "Set the meeting duration",
        "Input average hourly rate (or use defaults)",
        "View the true cost of your meeting",
      ],
    },
    faqs: [
      {
        question: "How is meeting cost calculated?",
        answer: "Meeting cost = (Number of attendees) × (Duration in hours) × (Average hourly rate). We also factor in preparation time and follow-up for comprehensive cost.",
      },
      {
        question: "What's the average meeting cost?",
        answer: "A typical 1-hour meeting with 6 people costs $300-500 in lost productivity. Many organizations waste 15-25% of productive time in unnecessary meetings.",
      },
      {
        question: "How can I reduce meeting costs?",
        answer: "Strategies: limit attendees to decision-makers, set strict time limits, require agendas, make meetings optional, use async updates for status, and end early if possible.",
      },
    ],
  },
  "collaboration-comparison": {
    howToUse: {
      steps: [
        "Review the feature comparison table",
        "Use filters to focus on specific capabilities",
        "Compare pricing for your team size",
        "Click 'Learn More' for detailed reviews",
      ],
    },
    faqs: [
      {
        question: "Slack vs Teams: which is better?",
        answer: "Slack excels at integrations and UX; Teams is better if you're already in the Microsoft ecosystem. For pure messaging, Slack wins. For unified workspace, Teams.",
      },
      {
        question: "Do I need both a PM tool and a chat tool?",
        answer: "Many teams use both: chat for real-time communication, PM tools for task tracking. Some tools like Notion and Monday combine both, reducing tool fatigue.",
      },
      {
        question: "What about free tiers?",
        answer: "Most tools offer free tiers with limits: Slack limits message history, Notion limits blocks, Trello limits power-ups. Evaluate if limits will impact your workflow.",
      },
    ],
  },
};

// Helper function to get tool enhancement data
export const getToolEnhancement = (toolId: string): ToolEnhancement | null => {
  return toolEnhancements[toolId] || null;
};
